import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect, useMemo } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import TableContainer from '@mui/material/TableContainer';
import { FormControlLabel, ListItemText, Stack, Switch, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { fCurrencyRupees } from 'src/utils/format-number';
import { hasPermission } from 'src/utils/has-permissions';
import { FACTORING_LIMITS, getSelectionViolations } from 'src/utils/cheque-factoring-helpers';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { createFactoringBatch, getFactoringCandidates } from 'src/redux/slices/cheque-factoring';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IFactoringCandidate } from 'src/types/cheque-factoring';

import CandidatesTableRow from '../candidates-table-row';
import CandidatesTableToolbar from '../candidates-table-toolbar';

const TABLE_HEAD = [
  { id: 'bank', label: 'Bank / Cheque Number', align: 'left', primary: true },
  { id: 'payerName', label: 'Payer', align: 'left', primary: true },
  { id: 'chequeDate', label: 'Cheque Date', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'debtorHeadroom', label: 'Debtor Headroom', align: 'right', primary: true },
];

export default function CandidatesView() {
  const dispatch = useAppDispatcher();
  const navigate = useNavigate();

  const { candidates } = useAppSelector((state) => state.chequeFactoring);

  const { user: authUser } = useAuthContext();
  // Admins may batch any In Hand cheque regardless of the factoring limits;
  // the server enforces this too, this only decides what the page offers.
  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const settings = useSettingsContext();

  const table = useTable({ defaultRowsPerPage: 25, defaultDense: true });

  const [generalSearch, setGeneralSearch] = useState('');
  const [batchDate, setBatchDate] = useState<Date | null>(new Date());
  const [isCreating, setIsCreating] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const denseHeight = table.dense ? 60 : 80;

  const fetchCandidates = useCallback(() => {
    dispatch(
      getFactoringCandidates({
        page: table.page,
        limit: table.rowsPerPage,
        generalSearch,
        sortBy: table.orderBy || 'chequeDate',
        sort: table.order,
        includeAll: isAdmin && showAll,
      })
    );
  }, [
    dispatch,
    table.page,
    table.rowsPerPage,
    table.orderBy,
    table.order,
    generalSearch,
    isAdmin,
    showAll,
  ]);

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCandidates]);

  const handleSearch = useCallback(
    (value: string) => {
      table.onResetPage();
      setGeneralSearch(value);
    },
    [table]
  );

  const selectedCandidates = useMemo(
    () =>
      candidates.items.filter((item: IFactoringCandidate) =>
        table.selected.includes(item._id)
      ),
    [candidates.items, table.selected]
  );

  const selectedTotalInCents = useMemo(
    () =>
      selectedCandidates.reduce(
        (sum: number, item: IFactoringCandidate) => sum + item.amountInCents,
        0
      ),
    [selectedCandidates]
  );

  const companyHeadroomInCents = useMemo(
    () => candidates.items[0]?.companyHeadroomInCents ?? FACTORING_LIMITS.COMPANY_HEADROOM_CENTS,
    [candidates.items]
  );

  const companyHeadroomAfterSelection = useMemo(
    () => companyHeadroomInCents - selectedTotalInCents,
    [companyHeadroomInCents, selectedTotalInCents]
  );

  const debtorKeyOf = (item: IFactoringCandidate) => `${item.bank}::${item.payerAccountNumber}`;

  // Returns why `item` can't be added to `current`, or null if it fits.
  const getSelectionViolation = useCallback(
    (item: IFactoringCandidate, current: IFactoringCandidate[]) => {
      const debtorSelected = current
        .filter((c) => debtorKeyOf(c) === debtorKeyOf(item))
        .reduce((sum, c) => sum + c.amountInCents, 0);
      if (debtorSelected + item.amountInCents > item.debtorHeadroomInCents) {
        return `Cheque ${item.chequeNumber} would exceed the debtor limit for ${
          item.payerName || item.payerAccountNumber
        } (headroom left: ${fCurrencyRupees(item.debtorHeadroomInCents - debtorSelected)})`;
      }
      const total = current.reduce((sum, c) => sum + c.amountInCents, 0);
      if (total + item.amountInCents > companyHeadroomInCents) {
        return `Cheque ${item.chequeNumber} would exceed the company limit (headroom left: ${fCurrencyRupees(
          companyHeadroomInCents - total
        )})`;
      }
      return null;
    },
    [companyHeadroomInCents]
  );

  const handleSelectRow = useCallback(
    (row: IFactoringCandidate) => {
      if (!isAdmin && !table.selected.includes(row._id)) {
        const violation = getSelectionViolation(row, selectedCandidates);
        if (violation) {
          enqueueSnackbar(violation, { variant: 'warning' });
          return;
        }
      }
      table.onSelectRow(row._id);
    },
    [table, isAdmin, selectedCandidates, getSelectionViolation]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        table.setSelected([]);
        return;
      }
      if (isAdmin) {
        table.setSelected(candidates.items.map((item: IFactoringCandidate) => item._id));
        return;
      }
      const accepted: IFactoringCandidate[] = [];
      candidates.items.forEach((item: IFactoringCandidate) => {
        if (!getSelectionViolation(item, accepted)) accepted.push(item);
      });
      if (accepted.length < candidates.items.length) {
        enqueueSnackbar(
          `Selected ${accepted.length} of ${candidates.items.length}; the rest don't fit the limits`,
          { variant: 'info' }
        );
      }
      table.setSelected(accepted.map((item) => item._id));
    },
    [table, isAdmin, candidates.items, getSelectionViolation]
  );

  // Only admins can end up with a violating selection (everyone else is
  // blocked at tick time), so the chips only ever show for them.
  const violations = useMemo(
    () =>
      isAdmin
        ? getSelectionViolations(selectedCandidates, companyHeadroomInCents)
        : new Map<string, string[]>(),
    [isAdmin, selectedCandidates, companyHeadroomInCents]
  );

  const handleToggleShowAll = useCallback(
    (checked: boolean) => {
      table.setSelected([]);
      table.onResetPage();
      setShowAll(checked);
    },
    [table]
  );

  const handleCreateBatch = useCallback(async () => {
    if (!batchDate) {
      enqueueSnackbar('Batch date is required', { variant: 'error' });
      return;
    }

    setIsCreating(true);

    try {
      const batch = await dispatch(
        createFactoringBatch({
          newBatch: {
            batchDate,
            chequeIds: table.selected,
          },
        })
      ).unwrap();

      enqueueSnackbar('Factoring batch created');
      table.setSelected([]);
      fetchCandidates();
      navigate(paths.dashboard.chequeFactoring.batches.view(batch._id));
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to create factoring batch', { variant: 'error' });
      }
    } finally {
      setIsCreating(false);
    }
  }, [batchDate, dispatch, fetchCandidates, navigate, table]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Factoring Candidates"
        links={[
          { name: 'Cheque Factoring', href: paths.dashboard.chequeFactoring.batches.root },
          { name: 'Candidates' },
        ]}
        action={
          <Stack direction="row" alignItems="center" spacing={3}>
            {isAdmin && (
              <FormControlLabel
                label="Show all cheques"
                control={
                  <Switch
                    checked={showAll}
                    onChange={(event) => handleToggleShowAll(event.target.checked)}
                  />
                }
              />
            )}

            <ListItemText
              primary="Company Headroom"
              secondary={fCurrencyRupees(companyHeadroomInCents)}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />
          </Stack>
        }
        sx={{ mb: { xs: 3 } }}
      />

      <Card>
        <CandidatesTableToolbar onSearch={handleSearch} />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden' }}>
          <TableSelectedAction
            dense={table.dense}
            numSelected={table.selected.length}
            rowCount={candidates.items.length}
            onSelectAllRows={handleSelectAll}
            action={
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ color: 'primary.main' }}>
                  Selected: {fCurrencyRupees(selectedTotalInCents)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: companyHeadroomAfterSelection < 0 ? 'error.main' : 'text.secondary',
                  }}
                >
                  Company headroom left: {fCurrencyRupees(companyHeadroomAfterSelection)}
                </Typography>
                <DatePicker
                  label="Batch Date"
                  value={batchDate}
                  onChange={(newValue) => setBatchDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <LoadingButton
                  variant="contained"
                  loading={isCreating}
                  onClick={handleCreateBatch}
                >
                  Create Batch
                </LoadingButton>
              </Stack>
            }
          />

          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                headLabel={TABLE_HEAD}
                rowCount={candidates.items.length}
                numSelected={table.selected.length}
                onSelectAllRows={handleSelectAll}
              />

              <TableBody>
                {candidates.status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((_, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {candidates.items.map((row: IFactoringCandidate) => (
                      <CandidatesTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onSelectRow={() => handleSelectRow(row)}
                        violations={violations.get(row._id)}
                      />
                    ))}
                  </>
                )}

                {candidates.status !== 'failure' && candidates.count === 0 && (
                  <TableNoData
                    message="No cheques eligible for factoring"
                    notFound={candidates.count === 0}
                  />
                )}

                {candidates.status === 'failure' && (
                  <TableErrorData notFound error={candidates.error} />
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={candidates.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
